// Nexora — rendu SVG A3 de l'Arbeitszeitliste, porté quasi tel quel depuis
// StoreFlowProV2/public/modules/arbeitszeitliste.js (même proportions viewBox 1587×1123,
// même vocabulaire allemand fixe — jamais traduit, § cahier des charges). Framework-agnostic
// (manipulation DOM directe sur un <svg> existant, comme le vanilla) : le composant React
// n'a qu'à fournir un ref + rappeler renderArbeitszeitlistePage() dans un useEffect, exactement
// comme index.html appelait renderPage() depuis son propre script. Document imprimable,
// volontairement indépendant du thème sombre/nuit choisi à l'écran — "le papier reste
// toujours clair" (même règle que tous les autres exports imprimés du projet).

const SVG_NS = "http://www.w3.org/2000/svg";

const C = {
  navy: "#0d1730", ink: "#211d16", muted: "#8f8571",
  line: "rgba(33,29,22,.16)", lineSoft: "rgba(33,29,22,.08)",
  weekendHead: "#26345f", weekendCell: "rgba(33,29,22,.05)",
  blue: "#364fe7", green: "#22c55e", red: "#ef4444", orange: "#e78a12", purple: "#7e22ce",
};

const BADGE_FILL = { LF: "#eaf7ef", V: "#f1ebff", K: "#ffebe9", U: "#eaf2ff", F: "#fff4e5" };
const BADGE_COLOR = { LF: C.green, V: C.purple, K: C.red, U: C.blue, F: C.orange };

const DOW_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const MONTH_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

function dowShort(dateISO) { return DOW_DE[new Date(dateISO + "T00:00:00").getDay()]; }
function isWeekend(dateISO) { const n = new Date(dateISO + "T00:00:00").getDay(); return n === 0 || n === 6; }
function fmtMinutes(minutes) {
  const m = Math.round(minutes || 0);
  const sign = m < 0 ? "-" : "";
  const abs = Math.abs(m);
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
}
// Colonne "Vertrag" (volume horaire contractuel hebdomadaire) — même style que fmtEuro,
// 1 décimale seulement si non entier (35 vs 37,5), jamais ".00" superflu sur un nombre rond.
function fmtHoursDe(hours) {
  return hours.toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}
function fmtEuro(value) {
  if (value === null || value === undefined) return "Nicht verfügbar";
  return value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function svgBuilder(svgEl) {
  function node(tag, attrs = {}, text) {
    const n = document.createElementNS(SVG_NS, tag);
    for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
    if (text !== undefined && text !== null) n.textContent = text;
    return n;
  }
  function add(n, parent) { (parent || svgEl).appendChild(n); return n; }
  function rect(x, y, w, h, fill = "#fff", stroke = C.line, rx = 8, sw = 1, parent) {
    return add(node("rect", { x, y, width: w, height: h, fill, stroke, "stroke-width": sw, rx }), parent);
  }
  function line(x1, y1, x2, y2, stroke = C.line, sw = 1, parent) {
    return add(node("line", { x1, y1, x2, y2, stroke, "stroke-width": sw }), parent);
  }
  function text(x, y, t, size = 12, weight = 400, fill = C.ink, anchor = "start", parent) {
    return add(node("text", { x, y, "font-size": size, "font-weight": weight, fill, "text-anchor": anchor, "font-family": "Inter,-apple-system,Segoe UI,Arial,sans-serif" }, t), parent);
  }
  function group(attrs = {}, className, parent) {
    const g = node("g", attrs);
    if (className) g.setAttribute("class", className);
    return add(g, parent);
  }
  function badge(x, y, label, kind, w = 24, parent) {
    const fill = BADGE_FILL[kind] || "#eef3fa";
    const color = BADGE_COLOR[kind] || C.ink;
    rect(x, y, w, 15, fill, color, 4, 1, parent);
    text(x + w / 2, y + 10.8, label, 7.6, 900, color, "middle", parent);
  }
  return { node, add, rect, line, text, group, badge };
}

function renderDayCell(b, x, y, colWidth, rowHeight, day) {
  if (!day) return;
  const code = day.absenceCode;

  if (code === "K" || code === "U" || code === "F") {
    const bw = Math.min(23, colWidth - 4);
    b.badge(x + (colWidth - bw) / 2, y + rowHeight / 2 - 7.5, code, code, bw);
    return;
  }

  const minutes = code === "V" ? day.vertretungMinutes : day.workedMinutes;
  const hasBadge = code === "LF" || code === "V";
  const numberY = hasBadge ? y + rowHeight * 0.42 : y + rowHeight / 2 + 2.5;

  if (minutes > 0) {
    b.text(x + colWidth / 2, numberY, fmtMinutes(minutes), Math.min(7.4, rowHeight * 0.24), 700, C.blue, "middle");
  }
  if (hasBadge) {
    const bw = Math.min(22, colWidth - 6);
    const bh = Math.min(15, rowHeight * 0.42);
    const by = y + rowHeight - bh - Math.max(2, rowHeight * 0.08);
    b.rect(x + (colWidth - bw) / 2, by, bw, bh, BADGE_FILL[code], BADGE_COLOR[code], 4, 1);
    b.text(x + colWidth / 2, by + bh * 0.75, code, Math.min(7.2, bh * 0.55), 900, BADGE_COLOR[code], "middle");
  }
}

function buildLayout(dateCount) {
  const tx = 28, ty = 224, tw = 1529;
  const fixedCols = [30, 144, 94, 58];
  const totalsCols = [62, 48, 48, 36, 36, 36, 48, 48];
  const fixedSum = fixedCols.reduce((a, b) => a + b, 0);
  const totalsSum = totalsCols.reduce((a, b) => a + b, 0);
  const dayW = (tw - fixedSum - totalsSum) / dateCount;

  const xPositions = [tx];
  fixedCols.forEach((w) => xPositions.push(xPositions.at(-1) + w));
  for (let d = 0; d < dateCount; d++) xPositions.push(xPositions.at(-1) + dayW);
  totalsCols.forEach((w) => xPositions.push(xPositions.at(-1) + w));

  return { tx, ty, tw, xPositions, dayW, fixedColCount: fixedCols.length, totalsColCount: totalsCols.length };
}

function renderQrCode(b, qr, x, y, boxSize) {
  if (!qr || !qr.size) {
    b.rect(x, y, boxSize, boxSize, "#fff", "#aab5c6", 8, 1);
    b.text(x + boxSize / 2, y + boxSize / 2 + 5, "QR", 15, 900, C.navy, "middle");
    return;
  }
  const quiet = 4;
  const totalModules = qr.size + quiet * 2;
  const cell = boxSize / totalModules;
  b.rect(x, y, boxSize, boxSize, "#fff", C.line, 4, 1);
  for (let row = 0; row < qr.size; row++) {
    for (let col = 0; col < qr.size; col++) {
      if (!qr.modules[row][col]) continue;
      b.rect(x + (col + quiet) * cell, y + (row + quiet) * cell, cell, cell, C.navy, "none", 0, 0);
    }
  }
}

function renderHeader(b, doc, qr, storeLabel) {
  b.text(30, 59, "NEXORA", 43, 900, C.navy);
  b.text(31, 82, "GÉRER, CONTRÔLER, PERFORMER", 10, 800, C.navy);
  b.text(335, 55, "ARBEITSZEITLISTE", 30, 900, C.navy);
  b.line(335, 65, 625, 65, C.orange, 2);
  b.text(335, 88, "Monatliche Erfassung der Arbeitszeiten", 14, 500, C.navy);
  b.text(335, 107, "und Abwesenheiten", 14, 500, C.navy);

  const monthLabel = `${MONTH_DE[doc.month - 1]} ${doc.year}`;
  const periodLabel = `01.${String(doc.month).padStart(2, "0")}.${doc.year} - ${String(doc.daysInMonth).padStart(2, "0")}.${String(doc.month).padStart(2, "0")}.${doc.year}`;
  const generatedLabel = new Date(doc.generatedAt).toLocaleDateString("de-DE");
  const responsableLabel = doc.responsable ? doc.responsable.name : "Nicht definiert";

  const cards = [
    [28, 124, 265, "FILIALE", storeLabel],
    [305, 124, 280, "VERANTWORTLICHER", responsableLabel],
    [597, 124, 210, "MONAT / JAHR", monthLabel],
    [819, 124, 174, "ERSTELLT AM", generatedLabel],
    [1005, 124, 250, "ZEITRAUM", periodLabel],
    [1267, 124, 290, "DOCUMENT ID", doc.id],
  ];
  cards.forEach(([x, y, w, l, v]) => {
    b.rect(x, y, w, 86, "#fff", C.line, 10);
    b.text(x + 18, y + 24, l, 9, 800, C.navy);
    b.text(x + 18, y + 52, v, 14, 800, C.ink);
  });

  renderQrCode(b, qr, 1468, 18, 88);
}

function renderTable(b, layout, dates, pageEmployees, rowHeight, startIndex) {
  const { tx, ty, tw, xPositions } = layout;
  const headH = 53;
  const th = headH + rowHeight * pageEmployees.length;

  b.rect(tx, ty, tw, th, "#fff", C.line, 8);
  b.rect(tx, ty, tw, headH, C.navy, C.navy, 8);

  ["Nr.", "Name, Vorname", "Funktion", "Herkunft"].forEach((h, i) => {
    b.text((xPositions[i] + xPositions[i + 1]) / 2, ty + 31, h, 9, 800, "#fff", "middle");
  });

  dates.forEach((dateISO, idx) => {
    const i = 4 + idx;
    const x = xPositions[i];
    const weekend = isWeekend(dateISO);
    if (weekend) b.rect(x, ty, layout.dayW, headH, C.weekendHead, C.weekendHead, 0, 0);
    const dayNum = dateISO.slice(8, 10);
    b.text(x + layout.dayW / 2, ty + 19, dayNum, 8, 800, "#fff", "middle");
    b.text(x + layout.dayW / 2, ty + 40, dowShort(dateISO), 7, 700, weekend ? "#ff9d9d" : "#dbe5ff", "middle");
  });

  const totalsHeaders = ["Σ Arbeit", "Σ LF", "Σ V", "Σ K", "Σ U", "Σ EZ", "Vertrag", "Δ"];
  totalsHeaders.forEach((h, j) => {
    const i = 4 + dates.length + j;
    b.text((xPositions[i] + xPositions[i + 1]) / 2, ty + 25, h, 7.6, 800, "#fff", "middle");
  });

  pageEmployees.forEach((emp, r) => {
    const y = ty + headH + r * rowHeight;
    if (emp.isExternal) b.rect(tx, y, tw, rowHeight, "#fff9ec", "none", 0, 0);
    else if (r % 2) b.rect(tx, y, tw, rowHeight, "#fbfaf6", "none", 0, 0);
    b.line(tx, y, tx + tw, y, C.lineSoft, .7);

    b.text((xPositions[0] + xPositions[1]) / 2, y + rowHeight / 2 + 3, String(startIndex + r + 1), 8, 700, C.ink, "middle");
    b.text(xPositions[1] + 7, y + rowHeight / 2 + 3, String(emp.displayName ?? ""), 8, 800, C.ink);
    b.text(xPositions[2] + 7, y + rowHeight / 2 + 3, String(emp.roleLabel ?? ""), 7.5, 500, C.ink);
    b.text((xPositions[3] + xPositions[4]) / 2, y + rowHeight / 2 + (emp.isExternal ? -1 : 3), emp.homeStoreId, 8, 600, emp.isExternal ? C.blue : C.ink, "middle");
    if (emp.isExternal) b.text((xPositions[3] + xPositions[4]) / 2, y + rowHeight / 2 + 10, "EXTERN", 6.2, 900, C.blue, "middle");

    dates.forEach((dateISO, idx) => {
      const i = 4 + idx;
      const x = xPositions[i];
      const day = emp.days[idx];
      if (isWeekend(dateISO)) b.rect(x, y, layout.dayW, rowHeight, C.weekendCell, "none", 0, 0);
      renderDayCell(b, x, y, layout.dayW, rowHeight, day);
    });

    const deltaKnown = emp.deltaMinutes !== null;
    const contractKnown = emp.weeklyContractedHours != null;
    const totalVals = [
      fmtMinutes(emp.workedMinutes), fmtMinutes(emp.lfMinutes), fmtMinutes(emp.vertretungMinutes),
      String(emp.krankDays), String(emp.urlaubDays), "0",
      contractKnown ? `${fmtHoursDe(emp.weeklyContractedHours)} h` : "—",
      deltaKnown ? (emp.deltaMinutes >= 0 ? "+" : "-") + fmtMinutes(Math.abs(emp.deltaMinutes)) : "—",
    ];
    totalVals.forEach((v, j) => {
      const i = 4 + dates.length + j;
      const color = j === 7 && deltaKnown && emp.deltaMinutes < 0 ? C.red : C.ink;
      b.text((xPositions[i] + xPositions[i + 1]) / 2, y + rowHeight / 2 + 3, v, 7.5, j < 3 ? 800 : 600, color, "middle");
    });
  });

  xPositions.slice(1, -1).forEach((x) => b.line(x, ty, x, ty + th, C.lineSoft, .5));
  b.line(tx, ty + headH, tx + tw, ty + headH, C.line, .8);

  return { th, headH };
}

const LEGEND = [
  ["LF", "Leistungsfrei"], ["KK", "Kinderkrank"], ["V", "Vertretung"], ["MS", "Mutterschutz"],
  ["K", "Krank"], ["SU", "Sonderurlaub"], ["U", "Urlaub"], ["EZ", "Einsatz extern"],
  ["F", "Feiertag"], ["UU", "Unbez. Urlaub"],
];

const ANOMALY_SEVERITY_COLOR = { critical: C.red, warning: C.orange, info: C.blue };
const VALIDATION_ROLE_LABEL = {
  employee: "Mitarbeiter", branchManager: "Filialleiter / FLA", areaManager: "Bereichsleiter",
};

function renderBottomCards(b, ctx) {
  const { document: doc, branchTotals, performance, anomalies = [], validation, page, pageCount, onAnomalyClick, onAnomalyMoreClick, onValidationSlotClick } = ctx;
  const by = 812, bh = 255;

  b.rect(28, by, 295, bh, "#fff", C.line, 10);
  b.text(46, by + 25, "LEGENDE", 11, 900, C.navy);
  LEGEND.forEach((it, i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const x = 46 + col * 143, y = by + 50 + row * 37;
    b.badge(x, y - 12, it[0], it[0], 26);
    b.text(x + 37, y, it[1], 8.2, 500, C.ink);
  });

  b.rect(335, by, 335, bh, "#fff", C.line, 10);
  b.text(353, by + 25, "ZUSAMMENFASSUNG", 11, 900, C.navy);
  b.text(365, by + 67, "Σ Arbeitsstunden", 9, 500, C.ink); b.text(365, by + 90, fmtMinutes(branchTotals.workedMinutes), 14, 900, C.ink);
  b.text(365, by + 128, "Σ Leistungsfrei", 9, 500, C.ink); b.text(365, by + 151, fmtMinutes(branchTotals.lfMinutes), 14, 900, C.ink);
  b.text(365, by + 189, "Σ Vertretung", 9, 500, C.ink); b.text(365, by + 212, fmtMinutes(branchTotals.vertretungMinutes), 14, 900, C.ink);
  b.text(520, by + 67, "Σ Krank", 9, 500, C.ink); b.text(630, by + 67, String(branchTotals.krankDays), 11, 900, C.ink, "end");
  b.text(520, by + 128, "Σ Urlaubstage", 9, 500, C.ink); b.text(630, by + 128, String(branchTotals.urlaubDays), 11, 900, C.ink, "end");
  b.text(520, by + 189, "Σ Einsatz extern", 9, 500, C.ink); b.text(630, by + 189, String(branchTotals.externalCount), 11, 900, C.ink, "end");

  b.rect(682, by, 215, bh, "#fff", C.line, 10);
  b.text(700, by + 25, "PERFORMANCE", 11, 900, C.navy);
  const naLabel = "Nicht verfügbar";
  b.text(710, by + 71, "Σ Umsatz", 9, 500, C.ink);
  b.text(873, by + 92, performance.umsatzTotal === null ? naLabel : fmtEuro(performance.umsatzTotal), performance.umsatzTotal === null ? 9 : 12, 900, performance.umsatzTotal === null ? C.muted : C.ink, "end");
  b.text(710, by + 132, "Σ Leistungsstunden", 9, 500, C.ink);
  b.text(873, by + 153, performance.leistungsstundenTotal === null ? naLabel : fmtMinutes(performance.leistungsstundenTotal * 60), performance.leistungsstundenTotal === null ? 9 : 12, 900, performance.leistungsstundenTotal === null ? C.muted : C.ink, "end");
  b.text(710, by + 193, "Ø Stundenleistung", 9, 500, C.ink);
  b.text(873, by + 214, performance.stundenleistung === null ? naLabel : fmtEuro(performance.stundenleistung) + "/h", performance.stundenleistung === null ? 9 : 12, 900, performance.stundenleistung === null ? C.muted : C.ink, "end");

  b.rect(909, by, 210, bh, "#fff", C.line, 10);
  b.text(927, by + 25, "ANOMALIEN", 11, 900, C.navy);
  const MAX_VISIBLE_ANOMALIES = 4;
  if (!anomalies.length) {
    b.text(927, by + 68, "Aucune anomalie détectée", 8.5, 700, C.green);
  } else {
    const visible = anomalies.slice(0, MAX_VISIBLE_ANOMALIES);
    const hasMore = anomalies.length > MAX_VISIBLE_ANOMALIES;
    const rowCount = hasMore ? MAX_VISIBLE_ANOMALIES : visible.length;
    visible.slice(0, rowCount).forEach((anomaly, i) => {
      const isLastVisibleRow = hasMore && i === rowCount - 1;
      const rowY = by + 60 + i * 43;
      const g = b.group({}, "azl-cell azl-anomaly-row");
      g.style.cursor = "pointer";
      b.rect(918, rowY - 15, 192, 38, "#fff", "none", 6, 0, g);
      const color = ANOMALY_SEVERITY_COLOR[anomaly.severity] || C.muted;
      b.rect(918, rowY - 15, 4, 38, color, "none", 0, 0, g);
      if (isLastVisibleRow) {
        b.text(937, rowY + 4, `+${anomalies.length - MAX_VISIBLE_ANOMALIES + 1} de plus`, 8.5, 800, C.blue, "start", g);
        g.addEventListener("click", () => onAnomalyMoreClick?.(anomalies));
      } else {
        b.text(937, rowY - 2, anomaly.message, 7.4, 600, C.ink, "start", g);
        g.addEventListener("click", () => onAnomalyClick?.(anomaly));
      }
    });
  }

  b.rect(1131, by, 426, bh, "#fff", C.line, 10);
  b.text(1149, by + 25, "VALIDIERUNG", 11, 900, C.navy);
  const sigByRole = new Map((validation?.signatures || []).map((s) => [s.validationRole, s]));
  ["employee", "branchManager", "areaManager"].forEach((role, i) => {
    const x = 1149 + i * 137;
    const sig = sigByRole.get(role);
    const g = b.group({}, sig ? "azl-cell azl-validation-slot signed" : "azl-cell azl-validation-slot");
    b.rect(x - 6, by + 46, 108, 100, "#fff", "none", 8, 0, g);
    b.text(x, by + 68, VALIDATION_ROLE_LABEL[role], 8.2, 700, C.ink, "start", g);
    if (sig) {
      b.text(x, by + 100, String(sig.userName ?? ""), 9.5, 800, C.green, "start", g);
      b.text(x, by + 116, new Date(sig.signedAt).toLocaleDateString("de-DE"), 7.5, 600, C.muted, "start", g);
      b.text(x, by + 130, new Date(sig.signedAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) + " Uhr", 7.5, 600, C.muted, "start", g);
    } else {
      b.line(x, by + 132, x + 102, by + 132, C.muted, 1, g);
      b.text(x, by + 177, "Datum:", 7.5, 500, C.ink, "start", g);
      b.line(x + 40, by + 177, x + 101, by + 177, C.muted, 1, g);
      if (!validation?.locked) {
        g.style.cursor = "pointer";
        g.addEventListener("click", () => onValidationSlotClick?.(role));
      }
    }
  });

  b.text(28, 1100, "LF/V partiel : heures travaillées + badge ; détail exact conservé dans Nexora", 8.5, 500, C.blue);
  b.text(793, 1100, "Vertraulich   |   Nur für interne Verwendung", 8.5, 500, C.blue, "middle");
  b.text(1557, 1100, `Seite ${page} von ${pageCount}`, 8.5, 500, C.blue, "end");
}

// ctx : { document, dates, pageEmployees, rowHeight, branchTotals, performance, anomalies,
//   validation, qr, pageIndex, pageCount, startIndex, storeLabel,
//   onAnomalyClick, onAnomalyMoreClick, onValidationSlotClick }
export function renderArbeitszeitlistePage(svgEl, ctx) {
  svgEl.innerHTML = "";
  svgEl.setAttribute("viewBox", "0 0 1587 1123");
  const b = svgBuilder(svgEl);
  b.rect(0, 0, 1587, 1123, "#fff", "none", 0, 0);

  renderHeader(b, ctx.document, ctx.qr, ctx.storeLabel);

  const layout = buildLayout(ctx.dates.length);
  renderTable(b, layout, ctx.dates, ctx.pageEmployees, ctx.rowHeight, ctx.startIndex);

  const isLastPage = ctx.pageIndex === ctx.pageCount - 1;
  if (isLastPage) {
    renderBottomCards(b, {
      document: ctx.document, branchTotals: ctx.branchTotals, performance: ctx.performance,
      anomalies: ctx.anomalies, validation: ctx.validation,
      page: ctx.pageIndex + 1, pageCount: ctx.pageCount,
      onAnomalyClick: ctx.onAnomalyClick, onAnomalyMoreClick: ctx.onAnomalyMoreClick,
      onValidationSlotClick: ctx.onValidationSlotClick,
    });
  } else {
    b.text(1557, 1100, `Page ${ctx.pageIndex + 1} / ${ctx.pageCount}`, 8.5, 500, C.blue, "end");
    b.text(28, 1100, "Suite page suivante", 8.5, 500, C.muted);
  }
}

// Traduit un clic écran (coordonnées CSS) en cellule jour/employé — même maths que
// wireCellClicks côté vanilla (mise à l'échelle du viewBox réel vs. la taille rendue).
export function resolveDayCellClick(svgEl, evt, dates, pageEmployees, rowHeight) {
  const layout = buildLayout(dates.length);
  const headH = 53;
  const rect = svgEl.getBoundingClientRect();
  const scaleX = 1587 / rect.width;
  const scaleY = 1123 / rect.height;
  const localX = (evt.clientX - rect.left) * scaleX;
  const localY = (evt.clientY - rect.top) * scaleY;
  if (localY < layout.ty + headH || localX < layout.tx) return null;

  const rowIndex = Math.floor((localY - layout.ty - headH) / rowHeight);
  const emp = pageEmployees[rowIndex];
  if (!emp) return null;

  const dayColStart = layout.xPositions[4];
  const dayColEnd = layout.xPositions[4 + dates.length];
  if (localX < dayColStart || localX >= dayColEnd) return null;
  const dayIndex = Math.floor((localX - dayColStart) / layout.dayW);
  const dateISO = dates[dayIndex];
  const day = emp.days[dayIndex];
  return { emp, dateISO, day };
}

export { fmtMinutes, fmtEuro, fmtHoursDe };
